import { useCallback, useEffect, useRef, useState } from "react";
import type { AsyncState } from "../app/use-async.js";
import type { SpaceModel } from "../api/types.js";

/**
 * The survey's Space model status, kept current while it is being built.
 *
 * `useAsync` would do for a status that never changes, but this one moves on
 * its own — queued, then processing with a progress fraction, then ready —
 * over a minute or two, and a reader who opened the survey to watch it should
 * see it arrive without pressing anything. So while the status is `queued` or
 * `processing` this re-reads it every `pollMs`, and stops the moment it
 * settles (`ready`, `failed`, or nothing captured).
 *
 * Unlike `useAsync`, a re-read never goes back to the loading state: the last
 * answer stays on screen while the next is fetched, so the progress bar does
 * not blink every few seconds. A failed re-read keeps the last answer too and
 * sets `stale`, rather than replacing a perfectly good status with an error
 * page because one poll hit a dropped connection; only the FIRST read's
 * failure is shown as a failure.
 */

export const SPACE_POLL_MS = 4_000;

export function isSpaceModelSettled(model: SpaceModel | null): boolean {
  return model === null || model.status === "ready" || model.status === "failed";
}

export interface SpaceModelPoll {
  readonly state: AsyncState<SpaceModel | null>;
  /** True when the latest re-read failed and the status shown is the last good one. */
  readonly stale: boolean;
  /** Re-reads now (the "Check again" button), without blanking the screen. */
  readonly refresh: () => void;
}

const LOADING = { status: "loading", data: null, error: null } as const;

export function useSpaceModel(
  load: () => Promise<SpaceModel | null>,
  /** Identifies what is being read (the survey id); a change starts over. */
  key: string,
  pollMs: number = SPACE_POLL_MS,
): SpaceModelPoll {
  const [state, setState] = useState<AsyncState<SpaceModel | null>>(LOADING);
  const [stale, setStale] = useState(false);
  const [nonce, setNonce] = useState(0);

  // The loader is rebuilt on every render by the caller; reading it through
  // a ref keeps the effect keyed on the survey alone.
  const loadRef = useRef(load);
  loadRef.current = load;
  const hasAnswer = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (hasAnswer.current !== key) {
      hasAnswer.current = null;
      setState(LOADING);
      setStale(false);
    }

    const read = async (): Promise<void> => {
      try {
        const data = await loadRef.current();
        if (cancelled) return;
        hasAnswer.current = key;
        setState({ status: "ready", data, error: null });
        setStale(false);
        if (!isSpaceModelSettled(data)) timer = setTimeout(() => void read(), pollMs);
      } catch (error) {
        if (cancelled) return;
        if (hasAnswer.current !== key) {
          setState({ status: "failed", data: null, error });
          return;
        }
        // Keep the last answer; try again, a little less often.
        setStale(true);
        timer = setTimeout(() => void read(), pollMs * 2);
      }
    };
    void read();

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [key, nonce, pollMs]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  return { state, stale, refresh };
}

/** The solver's stage names (`PhotogrammetrySession`), in words. */
export function spaceStageLabel(stage: string | null | undefined): string | null {
  switch (stage) {
    case "preProcessing":
      return "Preparing the photographs";
    case "imageAlignment":
      return "Lining the photographs up";
    case "pointCloudGeneration":
      return "Building a point cloud";
    case "meshGeneration":
      return "Building the mesh";
    case "textureMapping":
      return "Laying the photographs on as texture";
    case "optimization":
      return "Optimising the model";
    case null:
    case undefined:
    case "":
      return null;
    default:
      return stage;
  }
}

/** "21.3 MB" — for the size of the capture that was received. */
export function formatMegabytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
}
